import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { registerToastSink } from '@/lib/toast';
import { NotificationToast } from '@/components/notifications/NotificationToast';

interface Toast {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
}

interface ToastContextType {
  showToast: (toast: Omit<Toast, 'id'>) => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(7);
    setToasts((prev) => [...prev, { ...toast, id }].slice(-5)); // Limit to last 5 toasts
  }, []);

  const showSuccess = useCallback(
    (message: string, title = t('common.success', 'Success')) => {
      showToast({ title, message, type: 'success' });
    },
    [showToast, t],
  );

  const showError = useCallback(
    (message: string, title = t('common.error', 'Error')) => {
      showToast({ title, message, type: 'error' });
    },
    [showToast, t],
  );

  const showWarning = useCallback(
    (message: string, title = t('common.warning', 'Warning')) => {
      showToast({ title, message, type: 'warning' });
    },
    [showToast, t],
  );

  const showInfo = useCallback(
    (message: string, title = t('common.info', 'Info')) => {
      showToast({ title, message, type: 'info' });
    },
    [showToast, t],
  );

  // Lets `toast` from @/lib/toast reach this stack from outside React.
  useEffect(
    () =>
      registerToastSink((type, message) =>
        ({
          success: showSuccess,
          error: showError,
          warning: showWarning,
          info: showInfo,
        })[type](message),
      ),
    [showSuccess, showError, showWarning, showInfo],
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider
      value={{ showToast, showSuccess, showError, showWarning, showInfo }}
    >
      {children}
      <div className="fixed bottom-4 end-4 z-50 flex flex-col items-end gap-2">
        {toasts.map((toast) => (
          <NotificationToast
            key={toast.id}
            notification={{
              id: parseInt(toast.id, 10),
              title: toast.title,
              message: toast.message,
              type:
                toast.type === 'success'
                  ? 'appointment_approved'
                  : toast.type === 'error'
                    ? 'appointment_rejected'
                    : toast.type === 'warning'
                      ? 'appointment_reminder'
                      : 'appointment_request',
              createdAt: new Date(),
              updatedAt: new Date(),
              isRead: false,
            }}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}
